import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { Card, Button } from '../components/ui';
import { AlertCircle } from 'lucide-react';
import IframeAnalyzer from '../components/IframeAnalyzer';
import { SummaryForm, WebsiteForm, OfferingsForm, FormWrapper } from '../components/Forms';
import { useProfilePoll } from '../queries/onboardingPolls';
import { useCreateProfile } from '../mutations/onboarding';
import useSummaryAndOfferings from '../hooks/useSummaryAndOfferings';
import { validateURL } from '../utils/helpers';
import StepperWrapper from '../components/Onboarding/StepperWrapper';
import Tabs from '../components/ui/Tabs';
import PostModalContent from '../components/PostModal/PostModalContent';
import useMediaQuery from '../hooks/useMediaQuery';
import VideoPlayer from '../components/VideoPlayer';
import { useSinglePost } from '../hooks/useSinglePost';
import { set } from 'date-fns';

const OnboardingPage = () => {
  const { onboardingStep, setOnboardingStep, accessKey, websiteUrl, setWebsiteUrl } = useAppStore();
  const [websiteUrlInput, setWebsiteUrlInput] = useState(websiteUrl || '');
  const [error, setError] = useState(null);
  const [showWebsiteForm, setShowWebsiteForm] = useState(onboardingStep === 'not_started');
  const [profileProgress, setProfileProgress] = useState(35);
  const [shouldShowPostModal, setShouldShowPostModal] = useState(false);
  const navigate = useNavigate();
  const { isTablet } = useMediaQuery();

  // Use summary and offerings hook with conditional fetching
  const shouldFetchData = onboardingStep === 'completed';
  const {
    profileSummary,
    summaryLoading,
    summaryError,
    profileOfferings,
    offeringsLoading,
    offeringsError,
    handleSaveSummary,
    handleSaveOffering,
    handleDeleteOffering,
    updateSummaryMutation,
    updateOfferingMutation,
  } = useSummaryAndOfferings(shouldFetchData);

  // Use single post hook to get posts data and handlers
  // Only poll posts after profile polling is completed
  const {
    postsData,
    socialAccounts,
    regeneratedPost,
    isPostRegenerating,
    handleEditSubmit,
    handleScheduleSubmit,
    handleRegenerateSubmit,
    setPost,
  } = useSinglePost(shouldFetchData);

  // Extract first pending post from postsData
  const firstPostThatHasData = useMemo(() => {
    const tempPostData = postsData?.filter((post) => post.status === 'pending')[0];
    setShouldShowPostModal(!!tempPostData?.data && tempPostData?.status === 'pending');
    setPost(tempPostData);
    return tempPostData;
  }, [postsData]);
  // React Query mutations and polling hooks
  const createProfileMutation = useCreateProfile(
    () => {
      // On success, start polling for profile completion
      if (showWebsiteForm) setShowWebsiteForm(false);
      setOnboardingStep('polling_profile');
    },
    (error) => {
      // On error, show failed state
      setOnboardingStep('failed');
      setError(error.message);
    }
  );

  const profilePoll = useProfilePoll(
    accessKey,
    onboardingStep === 'polling_profile' && createProfileMutation.isSuccess
  );

  // Handle profile poll status changes and update progress
  useEffect(() => {
    if (profilePoll.data?.status) {
      const status = profilePoll.data.status;
      if (status === 'failed') {
        setOnboardingStep('failed');
        setError('Failed to create profile');
      } else if (status === 'new') {
        setProfileProgress(70);
      } else if (status === 'created') {
        setProfileProgress(100);
        setTimeout(() => {
          setOnboardingStep('completed');
        }, 1000);
      }
    }
    if (profilePoll.isError) {
      setOnboardingStep('failed');
      setError('Failed to create profile');
    }
  }, [profilePoll.data, profilePoll.isError, setOnboardingStep]);
  // Website URL form submission - Step 1
  const handleWebsiteSubmit = (e) => {
    e.preventDefault();
    setError(null);
    const { isValid, url } = validateURL(websiteUrlInput);
    if (!isValid) {
      setError('Please enter a valid website URL (e.g., https://example.com or example.com)');
      return;
    }

    try {
      setWebsiteUrl(url);
      // Start onboarding process (profile creation)
      createProfileMutation.mutate({ accessKey, websiteUrl: url });
    } catch (err) {
      setError(err.message || 'Failed to start onboarding');
      setOnboardingStep('failed_activation');
    }
  };

  // Initialize onboarding state based on current step
  useEffect(() => {
    // Only show website form if step is explicitly 'not_started'
    if (onboardingStep === 'not_started') {
      setShowWebsiteForm(true);
    }
  }, [onboardingStep]);

  if (showWebsiteForm) {
    return (
      <WebsiteForm
        error={error}
        loading={createProfileMutation.isPending}
        websiteUrlInput={websiteUrlInput}
        setWebsiteUrlInput={setWebsiteUrlInput}
        onSubmit={handleWebsiteSubmit}
      />
    );
  }

  return (
    <div className='min-h-[100vh]  bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-[20%]'>
      {onboardingStep === 'failed' && (
        <Card>
          <Card.Body>
            <div className='bg-red-50 border border-red-200 rounded-md p-4'>
              <div className='flex items-center space-x-2'>
                <AlertCircle className='w-5 h-5 text-red-600' />
                <span className='text-red-800 font-medium'>Onboarding Failed</span>
              </div>
              <p className='text-red-700 text-sm mt-1'>
                {error || 'Failed to create profile. Please try again.'}
              </p>
              <Button
                variant='primary'
                size='sm'
                className='mt-3'
                onClick={() => {
                  setShowWebsiteForm(true);
                  setError(null);
                  setOnboardingStep('not_started');
                }}
              >
                Try Again
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}
      {onboardingStep === 'polling_profile' || onboardingStep === 'completed' ? (
        <StepperWrapper
          className='h-[100%]'
          steps={[
            {
              title: 'Website Information',
              description: 'Analyzing your website content',
            },
            {
              title: 'Summary and Offerings',
              description: 'Review and finalize your business information',
            },
            { title: 'Generating Post', description: 'Generating your first post' },
          ]}
          onStepChange={(stepIndex) => {
            stepIndex === 0 && setOnboardingStep('completed');
          }}
          onFinish={() => {
            navigate('/dashboard', { replace: true });
          }}
          canProceed={(stepIndex) => {
            if (stepIndex === 0) {
              return profileProgress === 100;
            }
            if (stepIndex === 1) {
              return true;
            }
            return shouldShowPostModal;
          }}
          stepsWrapperClassName='p-0'
        >
          <Card>
            <Card.Body>
              <div className='space-y-6'>
                {/* Progress Bar */}
                <div className='space-y-2'>
                  <div className='flex justify-between text-sm'>
                    <span className='text-gray-600'>Creating Your Profile</span>
                    <span className='text-gray-900 font-medium'>{profileProgress}%</span>
                  </div>
                  <div className='w-full bg-gray-200 rounded-full h-2'>
                    <div
                      className='bg-primary-600 h-2 rounded-full transition-all duration-500 ease-out'
                      style={{ width: `${profileProgress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Show Iframe Analyzer */}
                <div className='space-y-3'>
                  {websiteUrlInput && (
                    <IframeAnalyzer
                      url={websiteUrlInput}
                      profileProgress={profileProgress}
                      className='mt-6'
                    />
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
          {isTablet ? (
            <Tabs
              className='flex bg-white/60 backdrop-blur-md rounded-2xl shadow-xl border border-white/20 p-3'
              tabs={[
                { id: 'summary', title: 'Summary' },
                { id: 'offerings', title: 'Offerings' },
              ]}
              initialActiveId={'summary'}
            >
              <FormWrapper
                isLoading={summaryLoading}
                error={summaryError}
                data={profileSummary}
                errorMessage='Failed to load summary data'
                tabId='summary'
              >
                <SummaryForm
                  initialData={profileSummary}
                  onSave={handleSaveSummary}
                  isLoading={updateSummaryMutation.isPending}
                />
              </FormWrapper>

              {/* Offerings Form - Right */}
              <FormWrapper
                isLoading={offeringsLoading}
                error={offeringsError}
                data={profileOfferings}
                errorMessage='Failed to load offerings data'
                tabId='offerings'
              >
                <OfferingsForm
                  initialData={profileOfferings || []}
                  onSave={handleSaveOffering}
                  onDelete={handleDeleteOffering}
                  isLoading={updateOfferingMutation.isPending}
                  className='pl-0'
                />
              </FormWrapper>
            </Tabs>
          ) : (
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 p-3 bg-white/60 backdrop-blur-md rounded-2xl shadow-xl border border-white/20'>
              {/* Summary Form - Left */}
              <FormWrapper
                isLoading={summaryLoading}
                error={summaryError}
                data={profileSummary}
                errorMessage='Failed to load summary data'
              >
                <SummaryForm
                  initialData={profileSummary}
                  onSave={handleSaveSummary}
                  isLoading={updateSummaryMutation.isPending}
                />
              </FormWrapper>

              {/* Offerings Form - Right */}
              <FormWrapper
                isLoading={offeringsLoading}
                error={offeringsError}
                data={profileOfferings}
                errorMessage='Failed to load offerings data'
              >
                <OfferingsForm
                  initialData={profileOfferings || []}
                  onSave={handleSaveOffering}
                  onDelete={handleDeleteOffering}
                  isLoading={updateOfferingMutation.isPending}
                />
              </FormWrapper>
            </div>
          )}

          {!shouldShowPostModal ? (
            <div className='flex flex-1 items-center justify-center p-6 bg-white/60 backdrop-blur-md rounded-2xl shadow-xl border border-white/20 overflow-hidden'>
              <VideoPlayer className='rounded-xl' />
            </div>
          ) : (
            <PostModalContent
              post={regeneratedPost || firstPostThatHasData}
              isLoading={isPostRegenerating}
              onRegenerateSubmit={handleRegenerateSubmit}
              socialAccounts={socialAccounts}
              onScheduleSubmit={handleScheduleSubmit}
              onEditSubmit={handleEditSubmit}
            />
          )}
        </StepperWrapper>
      ) : null}
    </div>
  );
};

export default OnboardingPage;
